export type VenueNoiseTransition = "enter-push-to-talk" | "return-to-normal";

export interface VenueNoiseDetectorOptions {
  speechThreshold: number;
  quietThreshold: number;
  continuousSpeechMs: number;
  occupancyWindowMs: number;
  minimumOccupancyWindowMs: number;
  maximumSpeechOccupancy: number;
  quietRecoveryMs: number;
  maximumSampleGapMs: number;
}

const DEFAULT_OPTIONS: VenueNoiseDetectorOptions = {
  speechThreshold: 0.50,
  quietThreshold: 0.35,
  continuousSpeechMs: 8_000,
  occupancyWindowMs: 15_000,
  minimumOccupancyWindowMs: 12_000,
  maximumSpeechOccupancy: 0.70,
  quietRecoveryMs: 30_000,
  maximumSampleGapMs: 1_000,
};

type ProbabilitySample = { atMs: number; speech: boolean };

export class VenueNoiseDetector {
  private readonly options: VenueNoiseDetectorOptions;
  private samples: ProbabilitySample[] = [];
  private continuousSpeechSince: number | null = null;
  private quietSince: number | null = null;
  private pushToTalk = false;
  private lastSampleAt: number | null = null;

  constructor(options: Partial<VenueNoiseDetectorOptions> = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  update(probability: number, atMs: number): VenueNoiseTransition | null {
    if (!Number.isFinite(probability) || !Number.isFinite(atMs)) return null;
    if (this.lastSampleAt !== null
      && (atMs <= this.lastSampleAt || atMs - this.lastSampleAt > this.options.maximumSampleGapMs)) {
      this.samples = [];
      this.continuousSpeechSince = null;
      this.quietSince = null;
    }
    this.lastSampleAt = atMs;
    if (this.pushToTalk) return this.updateRecovery(probability, atMs);

    const speech = probability >= this.options.speechThreshold;
    this.samples.push({ atMs, speech });
    const windowStart = atMs - this.options.occupancyWindowMs;
    while (this.samples[0]?.atMs < windowStart) this.samples.shift();

    if (speech) {
      this.continuousSpeechSince ??= atMs;
    } else {
      this.continuousSpeechSince = null;
    }

    const continuousSpeechMs = this.continuousSpeechSince === null
      ? 0 : atMs - this.continuousSpeechSince;
    const coveredMs = this.samples.length > 1 ? atMs - this.samples[0].atMs : 0;
    const speechSamples = this.samples.reduce((count, sample) => count + Number(sample.speech), 0);
    const speechOccupancy = this.samples.length === 0 ? 0 : speechSamples / this.samples.length;
    if (continuousSpeechMs < this.options.continuousSpeechMs
      && (coveredMs < this.options.minimumOccupancyWindowMs
        || speechOccupancy < this.options.maximumSpeechOccupancy)) return null;

    this.pushToTalk = true;
    this.samples = [];
    this.continuousSpeechSince = null;
    this.quietSince = probability <= this.options.quietThreshold ? atMs : null;
    return "enter-push-to-talk";
  }

  returnToNormal(): void {
    this.pushToTalk = false;
    this.samples = [];
    this.continuousSpeechSince = null;
    this.quietSince = null;
    this.lastSampleAt = null;
  }

  reset(): void {
    this.returnToNormal();
  }

  private updateRecovery(probability: number, atMs: number): VenueNoiseTransition | null {
    if (probability <= this.options.quietThreshold) {
      this.quietSince ??= atMs;
    } else {
      this.quietSince = null;
    }
    if (this.quietSince === null || atMs - this.quietSince < this.options.quietRecoveryMs) return null;
    this.returnToNormal();
    return "return-to-normal";
  }
}
